// Normaliza a versão publicada de um container (ContainerVersion da Tag Manager API v2)
// no formato usado pela página de relatório GTM

export interface GtmDetail {
	label: string
	value: string
}

export interface GtmCondition {
	variable: string
	operator: string
	operatorLabel: string
	negate: boolean
	value: string
}

export interface GtmTag {
	id: string
	name: string
	type: string
	typeLabel: string
	platform: string
	paused: boolean
	folder: string
	notes: string
	details: GtmDetail[]
	eventParameters: GtmDetail[]
	firingTriggerIds: string[]
	firingTriggers: string[]
	blockingTriggers: string[]
}

export interface GtmTrigger {
	id: string
	name: string
	type: string
	typeLabel: string
	folder: string
	notes: string
	conditions: GtmCondition[]
	tags: GtmTag[]
}

export interface GtmVariable {
	id: string
	name: string
	type: string
	typeLabel: string
	folder: string
	notes: string
	details: GtmDetail[]
}

export interface GtmPlatformCount {
	name: string
	count: number
}

export interface GtmReport {
	container: { publicId: string; name: string; tagManagerUrl: string }
	liveVersion: { id: string; name: string; description: string; tagManagerUrl: string }
	summary: {
		tags: number
		activeTags: number
		pausedTags: number
		ga4Tags: number
		triggers: number
		variables: number
		builtInVariables: number
	}
	tags: GtmTag[]
	triggers: GtmTrigger[]
	variables: GtmVariable[]
	builtInVariables: { name: string; type: string }[]
	platforms: GtmPlatformCount[]
	fetchedAt: Date
}

// Acionadores nativos do GTM: não aparecem na lista de triggers do container
export const BUILT_IN_TRIGGERS: Record<string, string> = {
	'2147479553': 'All Pages',
	'2147479572': 'Consent Initialization - All Pages',
	'2147479573': 'Initialization - All Pages',
}

const TAG_TYPES: Record<string, string> = {
	gaawe: 'GA4 - Evento',
	gaawc: 'GA4 - Configuração',
	hjtc: 'Hotjar - Código de acompanhamento',
	googtag: 'Tag do Google',
	html: 'HTML personalizado',
	img: 'Imagem personalizada',
	awct: 'Google Ads - Conversão',
	sp: 'Google Ads - Remarketing',
	gclidw: 'Vinculador de conversões',
	flc: 'Floodlight - Contador',
	fls: 'Floodlight - Vendas',
	ua: 'Universal Analytics',
}

const TRIGGER_TYPES: Record<string, string> = {
	pageview: 'Visualização de página',
	domReady: 'DOM pronto',
	windowLoaded: 'Janela carregada',
	customEvent: 'Evento personalizado',
	click: 'Clique - Todos os elementos',
	linkClick: 'Clique - Somente links',
	formSubmission: 'Envio de formulário',
	historyChange: 'Alteração no histórico',
	timer: 'Temporizador',
	scrollDepth: 'Profundidade de rolagem',
	youTubeVideo: 'Vídeo do YouTube',
	elementVisibility: 'Visibilidade do elemento',
	jsError: 'Erro de JavaScript',
	init: 'Inicialização',
	consentInit: 'Inicialização do consentimento',
	triggerGroup: 'Grupo de acionadores',
}

const VARIABLE_TYPES: Record<string, string> = {
	v: 'Variável da camada de dados',
	c: 'Constante',
	jsm: 'JavaScript personalizado',
	j: 'Variável JavaScript',
	k: 'Cookie primário',
	u: 'URL',
	f: 'Referenciador HTTP',
	e: 'Evento personalizado',
	aev: 'Variável de evento automática',
	d: 'Elemento DOM',
	smm: 'Tabela de consulta',
	remm: 'Tabela de regex',
	gas: 'Configurações do Google Analytics',
	awec: 'Dados fornecidos pelo usuário',
}

const OPERATORS: Record<string, string> = {
	equals: 'igual a',
	contains: 'contém',
	startsWith: 'começa com',
	endsWith: 'termina com',
	matchRegex: 'corresponde à regex',
	matchRegexIgnoreCase: 'corresponde à regex (ignora maiúsc.)',
	cssSelector: 'corresponde ao seletor CSS',
	greater: 'maior que',
	greaterOrEquals: 'maior ou igual a',
	less: 'menor que',
	lessOrEquals: 'menor ou igual a',
}

const TAG_DETAILS: Record<string, string> = {
	eventName: 'Evento',
	measurementIdOverride: 'ID de medição',
	measurementId: 'ID de medição',
	tagId: 'ID da tag',
	conversionId: 'ID de conversão',
	conversionLabel: 'rótulo',
	trackingId: 'ID de acompanhamento',
	advertiserId: 'advertiser',
	activityTag: 'atividade',
}

const VARIABLE_DETAILS: Record<string, string> = {
	name: 'Nome',
	value: 'Valor',
	component: 'Componente',
	varType: 'Tipo',
	attribute: 'Atributo',
	elementId: 'ID do elemento',
	selector: 'Seletor',
}

// Plataforma de cada tag: primeiro pelo tipo nativo do GTM, depois pelo nome
// (tags de terceiros costumam ser HTML personalizado ou modelos da galeria)
const PLATFORM_BY_TYPE: Record<string, string> = {
	gaawe: 'GA4',
	gaawc: 'GA4',
	ua: 'Universal Analytics',
	awct: 'Google Ads',
	sp: 'Google Ads',
	gclidw: 'Google Ads',
	flc: 'DV360',
	fls: 'DV360',
	hjtc: 'Hotjar',
}

const PLATFORM_BY_NAME: [RegExp, string][] = [
	[/\bga4\b|google analytics/, 'GA4'],
	[/google ads|\badwords\b/, 'Google Ads'],
	[/dv360|floodlight|campaign manager/, 'DV360'],
	[/\brd\b|rd station/, 'RD Station'],
	[/tiktok/, 'TikTok'],
	// "fb" sem \b: nomes como "Pixel_FB_..." usam "_", que conta como letra para o \b
	[/\bmeta\b|facebook|(^|[^a-z])fb([^a-z]|$)/, 'Meta'],
	[/hotjar/, 'Hotjar'],
	[/microsoft|clarity|\bbing\b|\buet\b/, 'Microsoft'],
	[/linkedin/, 'LinkedIn'],
	[/roi ?reveal/, 'ROI Reveal'],
	[/\bgrow\b/, 'Grow'],
]

export function buildReport(liveVersion: any, fetchedAt: Date): GtmReport {
	const folders = new Map<string, string>(
		(liveVersion.folder || []).map((f: any) => [f.folderId, f.name || ''])
	)
	const rawTriggers: any[] = liveVersion.trigger || []
	const triggerNames: Record<string, string> = { ...BUILT_IN_TRIGGERS }
	rawTriggers.forEach((t) => (triggerNames[t.triggerId] = t.name || ''))

	// Valores de variáveis constantes, para resolver IDs como {{GA - ID}} nas tags
	const constants: Record<string, string> = {}
	;(liveVersion.variable || [])
		.filter((v: any) => v.type === 'c')
		.forEach((v: any) => (constants[v.name] = (v.parameter || []).find((p: any) => p.key === 'value')?.value || ''))

	const tags = (liveVersion.tag || []).map((t: any) => normalizeTag(t, triggerNames, folders, constants))
	const triggers = rawTriggers.map((t) => normalizeTrigger(t, tags, folders))
	const variables = (liveVersion.variable || []).map((v: any) => normalizeVariable(v, folders))
	const builtInVariables = (liveVersion.builtInVariable || []).map((v: any) => ({
		name: v.name || '',
		type: v.type || '',
	}))
	const container = liveVersion.container || {}

	return {
		container: {
			publicId: container.publicId || '',
			name: container.name || '',
			tagManagerUrl: container.tagManagerUrl || '',
		},
		liveVersion: {
			id: liveVersion.containerVersionId || '',
			name: liveVersion.name || '',
			description: liveVersion.description || '',
			tagManagerUrl: liveVersion.tagManagerUrl || '',
		},
		summary: {
			tags: tags.length,
			activeTags: tags.filter((t: GtmTag) => !t.paused).length,
			pausedTags: tags.filter((t: GtmTag) => t.paused).length,
			ga4Tags: tags.filter((t: GtmTag) => t.platform === 'GA4').length,
			triggers: triggers.length,
			variables: variables.length,
			builtInVariables: builtInVariables.length,
		},
		tags,
		triggers,
		variables,
		builtInVariables,
		platforms: countPlatforms(tags),
		fetchedAt,
	}
}

function normalizeTag(
	tag: any,
	triggerNames: Record<string, string>,
	folders: Map<string, string>,
	constants: Record<string, string>
): GtmTag {
	const params = (tag.parameter || []).map((p: any) =>
		typeof p.value === 'string' ? { ...p, value: resolveConstants(p.value, constants) } : p
	)
	const firing: string[] = tag.firingTriggerId || []
	const blocking: string[] = tag.blockingTriggerId || []
	return {
		id: tag.tagId || '',
		name: tag.name || '',
		type: tag.type || '',
		typeLabel: typeLabel(TAG_TYPES, tag.type || ''),
		platform: tagPlatform(tag.type || '', tag.name || '', params),
		paused: !!tag.paused,
		folder: folders.get(tag.parentFolderId) || '',
		notes: tag.notes || '',
		details: details(params, TAG_DETAILS),
		eventParameters: eventParameters(params),
		firingTriggerIds: firing,
		firingTriggers: firing.map((id) => triggerNames[id] || id),
		blockingTriggers: blocking.map((id) => triggerNames[id] || id),
	}
}

function normalizeTrigger(trigger: any, tags: GtmTag[], folders: Map<string, string>): GtmTrigger {
	const conditions = [
		...(trigger.customEventFilter || []),
		...(trigger.autoEventFilter || []),
		...(trigger.filter || []),
	]
	return {
		id: trigger.triggerId || '',
		name: trigger.name || '',
		type: trigger.type || '',
		typeLabel: typeLabel(TRIGGER_TYPES, trigger.type || ''),
		folder: folders.get(trigger.parentFolderId) || '',
		notes: trigger.notes || '',
		conditions: conditions.map(normalizeCondition),
		tags: tags.filter((t) => t.firingTriggerIds.includes(trigger.triggerId)),
	}
}

function normalizeCondition(condition: any): GtmCondition {
	const params: Record<string, string> = {}
	;(condition.parameter || []).forEach((p: any) => (params[p.key] = p.value || ''))
	const operator = condition.type || ''
	const variable = params.arg0 || ''
	return {
		// {{_event}} é o nome interno da variável nativa "Event"
		variable: variable === '{{_event}}' ? '{{Event}}' : variable,
		operator,
		operatorLabel: OPERATORS[operator] || operator,
		negate: params.negate === 'true',
		value: params.arg1 || '',
	}
}

function normalizeVariable(variable: any, folders: Map<string, string>): GtmVariable {
	return {
		id: variable.variableId || '',
		name: variable.name || '',
		type: variable.type || '',
		typeLabel: typeLabel(VARIABLE_TYPES, variable.type || ''),
		folder: folders.get(variable.parentFolderId) || '',
		notes: variable.notes || '',
		details: details(variable.parameter || [], VARIABLE_DETAILS),
	}
}

function resolveConstants(value: string, constants: Record<string, string>): string {
	return value.replace(/\{\{([^}]+)\}\}/g, (ref, name) => constants[name] || ref)
}

function tagPlatform(type: string, name: string, params: any[]): string {
	if (PLATFORM_BY_TYPE[type]) {
		return PLATFORM_BY_TYPE[type]
	}
	if (type === 'googtag') {
		// A Tag do Google atende GA4 (G-...) e Google Ads (AW-...)
		const tagId: string = params.find((p) => p.key === 'tagId')?.value || ''
		if (tagId.startsWith('AW-')) {
			return 'Google Ads'
		}
		if (tagId.startsWith('G-')) {
			return 'GA4'
		}
	}

	const match = PLATFORM_BY_NAME.find(([pattern]) => pattern.test(name.toLowerCase()))
	return match ? match[1] : 'Outros'
}

function countPlatforms(tags: GtmTag[]): GtmPlatformCount[] {
	const counts = new Map<string, number>()
	tags.forEach((t) => counts.set(t.platform, (counts.get(t.platform) || 0) + 1))
	return [...counts.entries()]
		.map(([name, count]) => ({ name, count }))
		.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

function typeLabel(labels: Record<string, string>, type: string): string {
	return type.startsWith('cvt_') ? 'Modelo personalizado' : labels[type] || type
}

function details(parameters: any[], labels: Record<string, string>): GtmDetail[] {
	return parameters
		.filter((p) => labels[p.key] && p.value)
		.map((p) => ({ label: labels[p.key], value: p.value }))
}

function eventParameters(parameters: any[]): GtmDetail[] {
	// Tags GA4 guardam os parâmetros do evento numa lista de maps
	// ("eventSettingsTable" nas versões novas, "eventParameters" nas antigas)
	const result: GtmDetail[] = []
	parameters
		.filter((p) => p.key === 'eventSettingsTable' || p.key === 'eventParameters')
		.forEach((p) =>
			(p.list || []).forEach((item: any) => {
				const values: Record<string, string> = {}
				;(item.map || []).forEach((m: any) => (values[m.key] = m.value || ''))
				const name = values.parameter || values.name
				if (name) {
					result.push({ label: name, value: values.parameterValue || values.value || '' })
				}
			})
		)
	return result
}

// Cruzamento código × contêiner

export function conditionMatches(condition: GtmCondition, text: string): boolean {
	const { operator, value } = condition
	let result: boolean
	switch (operator) {
		case 'equals':
			result = text === value
			break
		case 'contains':
			result = text.includes(value)
			break
		case 'startsWith':
			result = text.startsWith(value)
			break
		case 'endsWith':
			result = text.endsWith(value)
			break
		case 'matchRegex':
		case 'matchRegexIgnoreCase':
			try {
				result = new RegExp(value, operator === 'matchRegexIgnoreCase' ? 'i' : '').test(text)
			} catch {
				result = false
			}
			break
		default:
			result = false
	}
	return condition.negate ? !result : result
}

// Acionadores de "Evento personalizado" que disparam para um dataLayer.push({ event })
export function triggersForEvent(triggers: GtmTrigger[], eventName: string): GtmTrigger[] {
	return triggers.filter((t) => {
		const eventConditions = t.conditions.filter((c) => c.variable === '{{Event}}')
		return (
			t.type === 'customEvent' &&
			eventConditions.length > 0 &&
			eventConditions.every((c) => conditionMatches(c, eventName))
		)
	})
}

// Acionadores de clique que olham para uma classe CSS (via {{Click Classes}} ou seletor CSS)
export function triggersForClickClass(triggers: GtmTrigger[], className: string): GtmTrigger[] {
	const selector = new RegExp(`\\.${className.replace(/[-]/g, '\\-')}(?![\\w-])`)
	return triggers.filter(
		(t) =>
			(t.type === 'click' || t.type === 'linkClick') &&
			t.conditions.some(
				(c) =>
					!c.negate &&
					(c.operator === 'cssSelector'
						? selector.test(c.value)
						: /Click Classes/i.test(c.variable) && conditionMatches(c, className))
			)
	)
}

// Nome do evento esperado por um acionador de "Evento personalizado" (quando é igualdade simples)
export function expectedEventName(trigger: GtmTrigger): string {
	const condition = trigger.conditions.find(
		(c) => c.variable === '{{Event}}' && c.operator === 'equals' && !c.negate
	)
	return condition ? condition.value : ''
}
